import { useAsyncEffect } from 'ahooks';
import { useRef } from 'react';
import { isActiveOrAllInnerTabsLoaded } from '../shared/utils';
import { useSpeakerPanelContext, useTabs } from '../store/context';
import { useAnalyticsSession } from '../store/context/AnalyticsSessionContext';

const useSpeakerPanel = () => {
  const { activeGlobalTab, globalTabs } = useTabs();
  const prevActiveGlobalTabRef = useRef(activeGlobalTab);
  const { setIsSpeakerPanelOpen } = useSpeakerPanelContext();
  const { isSessionIdle } = useAnalyticsSession();
  const activeSpeakerPanelId = activeGlobalTab?.group?.id;

  const closeIfSwitchedToAnotherSpeakerPanel = async () => {
    await window.dsDesktop.speakerPanel.closeFromSpeakerPanelIfOpen();
    await window.dsDesktop.speakerPanel.setToggleButtonState(false);
  };

  const initSpeakerPanel = async () => {
    if (isSessionIdle) {
      window.dsDesktop.speakerPanel.closeFromSpeakerPanelIfOpen({
        resetData: true,
      });
      window.dsDesktop.speakerPanel.setToggleButtonState(false);

      if (activeSpeakerPanelId) {
        window.dsDesktop.speakerPanel.resetData(activeSpeakerPanelId);
      }
      return;
    }

    // if it's not the tab with speaker panel, close any speaker panel view
    if (!activeSpeakerPanelId) {
      window.dsDesktop.speakerPanel.closeFromSpeakerPanelIfOpen();
      window.dsDesktop.speakerPanel.setToggleButtonState(false);
      return;
    }

    const speakerPanelData =
      await window.dsDesktop.speakerPanel.getData(activeSpeakerPanelId);

    if (
      prevActiveGlobalTabRef.current?.group &&
      activeGlobalTab.group &&
      prevActiveGlobalTabRef.current.group.id !== activeSpeakerPanelId
    ) {
      await closeIfSwitchedToAnotherSpeakerPanel();
    }

    if (!speakerPanelData) return;

    const activeSpeakerPanelTab = globalTabs.find(
      (tab) =>
        tab.group &&
        tab.group.id === activeSpeakerPanelId &&
        tab.group.mergingOptions?.key ===
          speakerPanelData.demosInfo?.[speakerPanelData.activePresentationIndex]
            ?.id
    );
    if (!activeSpeakerPanelTab) return;

    const isInnerTabLoaded = isActiveOrAllInnerTabsLoaded(
      activeSpeakerPanelTab
    );
    if (!isInnerTabLoaded) return;

    // Update animation settings, no matter if speaker panel was open or not,
    // right after the inner tab is loaded (isInnerTabLoaded === true)
    window.dsDesktop.demostacking.updateAnimationSettings({
      on: speakerPanelData.isMouseHighlightActive,
    });

    setIsSpeakerPanelOpen(speakerPanelData.isOpen);

    const isAppFocused = await window.dsDesktop.app.isAppFocused();

    if (speakerPanelData.isOpen && isAppFocused) {
      window.dsDesktop.speakerPanel.open();
    }
  };

  const handleSpeakerPanelChange = async () => {
    await initSpeakerPanel();
    prevActiveGlobalTabRef.current = activeGlobalTab;
  };

  useAsyncEffect(async () => {
    handleSpeakerPanelChange();
  }, [activeSpeakerPanelId, globalTabs, isSessionIdle]);
};

export default useSpeakerPanel;
